// Browse/search the account's other help articles and insert one as a link.
// Links use the public article path so they keep working on the published
// help center (Phase 2) — relative, so they're host-agnostic.

import { FileText, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog'

export interface LinkableArticle {
  slug: string
  title: string
  published: boolean
}

interface ArticleLinkDialogProps {
  open: boolean
  articles: LinkableArticle[]
  accountSlug: string
  onOpenChange: (open: boolean) => void
  onSubmit: (attrs: { href: string; title: string }) => void
}

export function articlePublicPath(accountSlug: string, articleSlug: string) {
  return `/help/${accountSlug}/articles/${articleSlug}`
}

export function ArticleLinkDialog({ open, articles, accountSlug, onOpenChange, onSubmit }: ArticleLinkDialogProps) {
  const [query, setQuery] = useState('')

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = q ? articles.filter(a => a.title.toLowerCase().includes(q)) : articles
    return list.slice(0, 50)
  }, [articles, query])

  const pick = (article: LinkableArticle) => {
    onSubmit({ href: articlePublicPath(accountSlug, article.slug), title: article.title })
    onOpenChange(false)
    setQuery('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Link to an article</DialogTitle>
          <DialogDescription>Inserts a link to another help center article.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search articles…" className="pl-8" />
        </div>

        <div className="max-h-72 overflow-y-auto rounded-md border border-border">
          {results.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">No matching articles.</p>
          ) : (
            results.map(article => (
              <button
                key={article.slug}
                type="button"
                onClick={() => pick(article)}
                className="flex w-full cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50"
              >
                <FileText className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{article.title}</span>
                {!article.published && <Badge variant="secondary" className="ml-auto shrink-0">Draft</Badge>}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
